import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loading, PageHeader, EmptyState } from "../../components/shared";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Search } from "lucide-react";

const TONE = {
  CONFIRMED: "bg-emerald-100 text-emerald-800",
  PENDING_CONFIRMATION: "bg-amber-100 text-amber-800",
  RESCHEDULE_REQUESTED: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-muted text-muted-foreground",
};
const FILTERS = ["ALL", "CONFIRMED", "CANCELLED"];
const fmtIST = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) : "—";

export default function AdminExpertAppointments() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = (s = status, query = q) =>
    api.get("/admin/expert-appointments", { params: { status: s === "ALL" ? "" : s, q: query } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load("ALL", ""); }, []);

  const view = async (id) => {
    try { setDetail((await api.get(`/admin/expert-appointments/${id}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load appointment"); }
  };

  if (!rows) return <Loading />;
  return (
    <div>
      <PageHeader title="Expert Appointments" subtitle="Oversight of all scheduled consultation appointments." />
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-appt-search" className="pl-9" placeholder="Search ID, expert, service or society" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select data-testid="admin-appt-status" className="h-10 border rounded-md px-3 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); load(e.target.value, q); }}>
          {FILTERS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <Button data-testid="admin-appt-search-btn" onClick={() => load()} className="gap-2"><Search className="w-4 h-4" />Search</Button>
      </div>

      {rows.length === 0 ? <EmptyState icon={CalendarClock} title="No appointments" subtitle="Scheduled appointments will appear here." /> : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm" data-testid="admin-appts-table">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr><th className="p-3">Appointment</th><th className="p-3">Society</th><th className="p-3">Expert</th><th className="p-3">Service</th><th className="p-3">When (IST)</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.appointment_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => view(r.appointment_id)} data-testid={`admin-appt-${r.appointment_id}`}>
                  <td className="p-3 font-medium">{r.display_id}</td>
                  <td className="p-3">{r.society_name || r.society_id}</td>
                  <td className="p-3">{r.expert_snapshot?.name}</td>
                  <td className="p-3">{r.service_snapshot?.name}</td>
                  <td className="p-3 text-xs">{fmtIST(r.scheduled_start_utc)}</td>
                  <td className="p-3"><span className={`text-xs rounded-full px-2.5 py-1 ${TONE[r.status]}`}>{r.status.replace("_", " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-appt-detail">
          <DialogHeader><DialogTitle className="font-display">Appointment {detail?.display_id}</DialogTitle><DialogDescription className="sr-only">Read-only appointment oversight</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="font-semibold">{detail.expert_snapshot?.name}</span><span className={`text-xs rounded-full px-2.5 py-1 ${TONE[detail.status]}`}>{detail.status.replace("_", " ")}</span></div>
              <Row label="Appointment ID" value={detail.display_id} />
              <Row label="Request ID" value={detail.consultation_request_id} />
              <Row label="Society" value={detail.society_name || detail.society_id} />
              <Row label="Service" value={detail.service_snapshot?.name} />
              <Row label="When" value={`${fmtIST(detail.scheduled_start_utc)} (IST)`} />
              <Row label="Duration" value={`${detail.duration_minutes} min`} />
              {detail.cancellation_reason && <Row label="Cancel reason" value={detail.cancellation_reason} />}
              <div>
                <p className="text-xs text-muted-foreground mb-1 mt-2">History</p>
                <ul className="space-y-1">{(detail.history || []).map((h, i) => <li key={i} className="text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{h.action}{h.to ? ` → ${fmtIST(h.to)}` : ""} · {fmtIST(h.at)}</li>)}</ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex gap-2"><span className="text-muted-foreground min-w-[120px]">{label}</span><span className="font-medium break-all">{value}</span></div>;
}
